
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pdf } from '@react-pdf/renderer';
import FinalInspectionReportPDF from '../components/FinalInspectionReportPDF';
import MigrosFinalInspectionReportPDF from '../components/MigrosFinalInspectionReportPDF';
import FactoryFinalInspectionReportPDF from '../components/FactoryFinalInspectionReportPDF';
import { useAllowedReportTemplates } from '../utils/reportTemplates';

type PdfTemplate = 'qa-mate' | 'migros' | 'factory';
type PdfCompression = 1 | 2 | 3 | 4;

interface ChecklistItem {
  id: string;
  title: string;
  description?: string | null;
  category?: string | null;
  status?: 'pass' | 'fail' | 'na' | null;
  remarks?: string | null;
  evidence_images?: string[];
  evidence_required?: boolean;
}

interface DefectItem {
  id: string;
  sku_id?: string | null;
  type?: string | null;
  title?: string | null;
  category?: string | null;
  severity?: string | null;
  level?: string | null;
  count?: number;
  location?: string | null;
  description?: string | null;
  evidence_images?: string[];
}

interface SkuItem {
  id: string;
  po_sku_id?: string | null;
  sku_code?: string | null;
  sku_name?: string | null;
  description?: string | null;
  sku_description?: string | null;
  ordered_qty?: number;
  quantity?: number;
  inspected_qty?: number;
  result?: 'pass' | 'fail' | 'pending';
  notes?: string | null;
  colour?: string | null;
  color?: string | null;
  size?: string | null;
  product_category?: string | null;
  productCategory?: string | null;
  vendor_style?: string | null;
  vendorStyle?: string | null;
  production_completion?: number;
  productionCompletion?: number;
  metal_detection?: string | null;
  metalDetection?: string | null;
  defects?: DefectItem[];
  photoTiles?: PhotoItem[];
  photo_tiles?: PhotoItem[];
  photos?: PhotoItem[];
}

interface PhotoItem {
  id: string;
  sku_id?: string | null;
  tile_key?: string | null;
  label?: string | null;
  tile_label?: string | null;
  image_url: string;
  imagePath?: string | null;
  localPath?: string | null;
  caption?: string | null;
  is_mandatory?: boolean;
  required?: boolean;
}

interface AuditItem {
  id: string;
  sku_id?: string | null;
  type: 'MEASUREMENT' | 'WEIGHT' | 'METAL_DETECTION';
  data?: any;
  result?: 'pass' | 'fail' | 'pending';
  notes?: string | null;
  remarks?: string | null;
}

interface FinalInspectionDetail {
  id: string;
  po_number?: string | null;
  customer_name?: string | null;
  product_category?: string | null;
  po_ship_date?: string | null;
  po_notes?: string | null;
  inspection_type?: string | null;
  inspection_level?: string | null;
  aql_major?: string | null;
  aql_minor?: string | null;
  aql_critical?: string | null;
  lot_size?: number;
  remarks?: string | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  overall_result: 'PASS' | 'FAIL' | 'PENDING';
  is_recovered?: boolean;
  recovery_incomplete?: boolean;
  recovered_at?: string | null;
  inspector_name?: string | null;
  inspector_signature_url?: string | null;
  factory_rep_signature_url?: string | null;
  facility_name?: string | null;
  facility?: { id: string; name: string } | null;
  additional_documents?: string[];
  created_at: string;
  inspector?: { id: string; name: string; profileImage?: string | null };
  checklists?: ChecklistItem[];
  defects?: DefectItem[];
  skus?: SkuItem[];
  photos?: PhotoItem[];
  audits?: AuditItem[];
}

const getInitials = (name?: string | null) => {
  if (!name) return 'NA';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
};

const formatDate = (raw?: string | null) => {
  if (!raw) return 'N/A';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const FinalInspectionReportDetail: React.FC = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const focusSku = searchParams.get('sku');
  const { token, logout } = useAuth();
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const zoomContainerRef = useRef<HTMLDivElement>(null);
  const [inspection, setInspection] = useState<FinalInspectionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfStatusIndex, setPdfStatusIndex] = useState(0);
  const [pdfProgress, setPdfProgress] = useState<{ done: number; total: number } | null>(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [imageStatusIndex, setImageStatusIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<PdfTemplate>('qa-mate');
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedCompression, setSelectedCompression] = useState<PdfCompression>(2);
  const [isCompressionDropdownOpen, setIsCompressionDropdownOpen] = useState(false);
  const compressionDropdownRef = useRef<HTMLDivElement>(null);

  const templateOptions: { value: PdfTemplate; name: string; icon: string; iconBg: string; iconColor: string; description: string }[] = [
    {
      value: 'qa-mate',
      name: 'QA Mate Template',
      icon: 'verified',
      iconBg: 'bg-blue-100',
      iconColor: 'text-[#136bce]',
      description: 'Default QA Mate inspection report layout.',
    },
    {
      value: 'migros',
      name: 'Migros Template',
      icon: 'inventory_2',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      description: 'Migros Asia — Final Random Inspection (Textile/Hometextile) Version-1 layout.',
    },
    {
      value: 'factory',
      name: 'Factory Template (KKP Fine Linen)',
      icon: 'factory',
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-700',
      description: 'KKP Fine Linen "New Format 2026" — branded cover, SKU Inspection Summary + Defect Summary, AQL accept totals.',
    },
  ];
  const allowedTemplates = useAllowedReportTemplates();
  const visibleTemplateOptions = templateOptions.filter(o => allowedTemplates.includes(o.value));
  const selectedTemplateOption =
    visibleTemplateOptions.find(t => t.value === selectedTemplate) ??
    visibleTemplateOptions[0] ??
    templateOptions[0];
  useEffect(() => {
    if (visibleTemplateOptions.length && !visibleTemplateOptions.some(o => o.value === selectedTemplate)) {
      setSelectedTemplate(visibleTemplateOptions[0].value);
    }
  }, [visibleTemplateOptions, selectedTemplate]);

  const compressionOptions: {
    value: PdfCompression;
    name: string;
    icon: string;
    iconBg: string;
    iconColor: string;
    description: string;
    maxDim: number;
    quality: number;
  }[] = [
    {
      value: 1,
      name: 'Extreme Compression',
      icon: 'compress',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      description: 'Less quality, high compression — smallest file size.',
      maxDim: 1000,
      quality: 0.4,
    },
    {
      value: 2,
      name: 'Recommended Compression',
      icon: 'recommend',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      description: 'Good quality, good compression — best for sharing.',
      maxDim: 1600,
      quality: 0.6,
    },
    {
      value: 3,
      name: 'Less Compression',
      icon: 'photo_size_select_large',
      iconBg: 'bg-blue-100',
      iconColor: 'text-[#136bce]',
      description: 'High quality, less compression — larger file size.',
      maxDim: 2400,
      quality: 0.82,
    },
    {
      value: 4,
      name: 'Original',
      icon: 'high_quality',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600',
      description: 'The original document quality — no compression applied.',
      maxDim: 0,
      quality: 1,
    },
  ];
  const selectedCompressionOption = compressionOptions.find(c => c.value === selectedCompression)!;

  const pdfStatusMessages = [
    'Preparing inspection data…',
    'Securing report metadata…',
    'Fetching evidence images…',
    'Compiling defect findings…',
    'Embedding signatures…',
    'Rendering high-quality pages…',
    'Almost there — finalizing your PDF…',
  ];

  const imageStatusMessages = [
    'Collecting image list…',
    'Fetching inspection photos…',
    'Fetching SKU photos…',
    'Fetching defect evidence…',
    'Fetching checklist evidence…',
    'Compressing into ZIP archive…',
    'Almost done — finalizing your download…',
  ];

  useEffect(() => {
    if (!generatingPDF) {
      setPdfStatusIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setPdfStatusIndex((i) => (i + 1) % pdfStatusMessages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [generatingPDF]);

  // When arriving from a "View Report" link with ?sku=<po_sku_id>, scroll to that
  // SKU's detail card once the report has loaded.
  useEffect(() => {
    if (!inspection || !focusSku) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`sku-${focusSku}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    return () => clearTimeout(t);
  }, [inspection, focusSku]);

  useEffect(() => {
    if (!downloadingImages) { setImageStatusIndex(0); return; }
    const interval = setInterval(() => setImageStatusIndex(i => (i + 1) % imageStatusMessages.length), 2500);
    return () => clearInterval(interval);
  }, [downloadingImages]);

  const openPreview = useCallback((url: string | null) => {
    setZoom(1);
    setPreviewImage(url);
  }, []);

  useEffect(() => {
    if (!isTemplateDropdownOpen) return;
    const onClick = (e: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node)) {
        setIsTemplateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isTemplateDropdownOpen]);

  useEffect(() => {
    if (!isCompressionDropdownOpen) return;
    const onClick = (e: MouseEvent) => {
      if (compressionDropdownRef.current && !compressionDropdownRef.current.contains(e.target as Node)) {
        setIsCompressionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isCompressionDropdownOpen]);

  useEffect(() => {
    const el = zoomContainerRef.current;
    if (!el || !previewImage) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      setZoom((prev) => Math.min(Math.max(0.25, prev - e.deltaY * 0.001), 5));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [previewImage]);

  useEffect(() => {
    const fetchInspection = async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const response = await fetch(`${baseUrl}/api/v1/final-inspections/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (response.status === 401) {
          logout();
          return;
        }
        const data = await response.json();
        if (data.success) {
          setInspection(data.data);
        } else {
          setError(data.message || 'Failed to load inspection');
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load inspection');
      } finally {
        setLoading(false);
      }
    };

    if (token && id) fetchInspection();
  }, [token, id]);

  const handleDownload = () => {
    setTimeout(() => { window.print(); }, 100);
  };

  // Convert any image URL to a base64 data URL so @react-pdf/renderer can
  // embed it without hitting CORS or auth barriers.
  // Strategy:
  //   1. Try fetch WITHOUT auth (public storage: S3, Supabase public bucket, CDN)
  //   2. If that fails, retry WITH auth header (API-served / private URLs)
  //   3. If both fail, return the original URL as last resort
  const toDataUrl = async (url: string): Promise<string> => {
    if (!url || url.startsWith('data:')) return url;

    const blobToDataUrl = (blob: Blob): Promise<string> =>
      new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result as string);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      });

    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    // GCS URLs are blocked by browser CORS — route them through the API proxy.
    const isGcs = url.includes('storage.googleapis.com');
    const fetchUrl = isGcs
      ? `${baseUrl}/api/v1/proxy-image?url=${encodeURIComponent(url)}`
      : url;

    try {
      const res = await fetch(fetchUrl, { mode: 'cors' });
      if (res.ok) return await blobToDataUrl(await res.blob());
      console.warn('[PDF img] fetch failed', res.status, fetchUrl);
    } catch (e) {
      console.warn('[PDF img] fetch threw', e, fetchUrl);
    }

    console.error('[PDF img] could NOT convert, returning raw URL — image will likely be missing:', url);
    return url;
  };

  // Re-encode a base64 image data URL through a canvas to shrink it for the PDF.
  // `maxDim` caps the longest edge (0 = don't resize); `quality` is the JPEG
  // quality (0–1). SVGs and non-image data URLs are returned untouched, and the
  // original is kept whenever re-encoding wouldn't actually make it smaller.
  const compressDataUrl = (dataUrl: string, maxDim: number, quality: number): Promise<string> =>
    new Promise((resolve) => {
      if (!dataUrl.startsWith('data:image/') || dataUrl.startsWith('data:image/svg')) {
        resolve(dataUrl);
        return;
      }
      const img = new Image();
      img.onload = () => {
        try {
          const longest = Math.max(img.width, img.height) || 1;
          const scale = maxDim > 0 ? Math.min(1, maxDim / longest) : 1;
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) { resolve(dataUrl); return; }
          // White matte so transparent PNGs don't come out black once flattened to JPEG.
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          const out = canvas.toDataURL('image/jpeg', quality);
          resolve(out.length > 0 && out.length < dataUrl.length ? out : dataUrl);
        } catch {
          resolve(dataUrl);
        }
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });

  const handleGeneratePDF = async (
    template: PdfTemplate = 'qa-mate',
    compression: PdfCompression = 2,
  ) => {
    if (!inspection) return;
    setGeneratingPDF(true);
    const compressionCfg = compressionOptions.find(c => c.value === compression)!;
    const shouldCompress = compression < 4;
    try {
      // Deep-clone so we never mutate the displayed state
      const clone: FinalInspectionDetail = JSON.parse(JSON.stringify(inspection));

      // URL dedup cache — stores in-flight promises (not resolved values) so
      // concurrent requests for the same URL share one fetch instead of racing
      // duplicate fetches of it.
      const cache = new Map<string, Promise<string>>();
      const resolveUrl = (url: string | null | undefined): Promise<string> => {
        if (!url) return Promise.resolve('');
        if (cache.has(url)) return cache.get(url)!;
        const promise = (async () => {
          let data = await toDataUrl(url);
          if (shouldCompress && data.startsWith('data:image/')) {
            data = await compressDataUrl(data, compressionCfg.maxDim, compressionCfg.quality);
          }
          return data;
        })();
        cache.set(url, promise);
        return promise;
      };

      // Collect every image-resolving assignment as a task, then run them with
      // bounded concurrency instead of one-at-a-time — same fetch/compress
      // logic per image, just no longer waiting for each to finish before
      // starting the next.
      type ResolveTask = () => Promise<void>;
      const tasks: ResolveTask[] = [];
      const queueUrl = (getUrl: () => string | null | undefined, setUrl: (v: string) => void) => {
        const url = getUrl();
        if (!url) return;
        tasks.push(async () => { setUrl(await resolveUrl(url)); });
      };
      const queueStrArr = (arr: string[] | undefined) => {
        if (!arr) return;
        for (let i = 0; i < arr.length; i++) {
          if (arr[i]) queueUrl(() => arr[i], (v) => { arr[i] = v; });
        }
      };

      // ── Global photos ──────────────────────────────────────────────────────
      for (const p of clone.photos || []) {
        queueUrl(() => p.image_url, (v) => { p.image_url = v; });
      }

      // ── Global defect evidence ─────────────────────────────────────────────
      for (const d of clone.defects || []) queueStrArr(d.evidence_images);

      // ── Checklist evidence ─────────────────────────────────────────────────
      for (const c of clone.checklists || []) queueStrArr(c.evidence_images);

      // ── Audit images (Measurement / Weight audit evidence) ─────────────────
      for (const a of clone.audits || []) {
        if (!a.data) continue;
        for (const key of ['imageUrls', 'image_urls', 'evidence_images', 'images', 'photos'] as const) {
          if (Array.isArray(a.data[key])) queueStrArr(a.data[key]);
        }
      }

      // ── SKU-level photos & defects ─────────────────────────────────────────
      for (const sku of clone.skus || []) {
        for (const arr of [sku.photoTiles, sku.photo_tiles, sku.photos]) {
          for (const p of arr || []) {
            queueUrl(() => p.image_url, (v) => { p.image_url = v; });
          }
        }
        for (const d of sku.defects || []) queueStrArr(d.evidence_images);
      }

      // ── Signatures ─────────────────────────────────────────────────────────
      queueUrl(() => clone.inspector_signature_url, (v) => { clone.inspector_signature_url = v; });
      queueUrl(() => clone.factory_rep_signature_url, (v) => { clone.factory_rep_signature_url = v; });

      // Run with bounded concurrency (6 at a time — matches the browser's own
      // per-host connection limit, so this doesn't overwhelm anything new),
      // reporting real progress as each image finishes.
      setPdfProgress({ done: 0, total: tasks.length });
      const CONCURRENCY = 6;
      let nextTask = 0;
      let completed = 0;
      const worker = async () => {
        while (nextTask < tasks.length) {
          const task = tasks[nextTask++];
          await task();
          completed++;
          setPdfProgress({ done: completed, total: tasks.length });
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, tasks.length) }, worker));

      const docElement = template === 'migros'
        ? <MigrosFinalInspectionReportPDF inspection={clone} />
        : template === 'factory'
        ? <FactoryFinalInspectionReportPDF inspection={clone} />
        : <FinalInspectionReportPDF inspection={clone} />;
      const filenamePrefix = template === 'migros'
        ? 'Migros-Final-Inspection'
        : template === 'factory'
        ? 'KKP-Final-Inspection'
        : 'Final-Inspection';
      const blob = await pdf(docElement).toBlob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `${filenamePrefix}-${inspection.po_number || inspection.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(dlUrl);
    } catch (err: any) {
      console.error('PDF generation failed:', err);
      alert(`PDF generation failed: ${err?.message || 'Unknown error'}. Check browser console for details.`);
    } finally {
      setGeneratingPDF(false);
      setPdfProgress(null);
    }
  };

  const handleDownloadImages = async () => {
    if (!inspection) return;
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

    const fetchBlob = async (url: string): Promise<Blob | null> => {
      if (!url) return null;
      let abs = url;
      if (!/^(https?:|blob:|data:)/.test(abs)) abs = `${baseUrl}${abs.startsWith('/') ? '' : '/'}${abs}`;
      const isGcs = abs.includes('storage.googleapis.com');
      const fetchUrl = isGcs ? `${baseUrl}/api/v1/proxy-image?url=${encodeURIComponent(abs)}` : abs;
      try {
        const res = await fetch(fetchUrl, { mode: 'cors' });
        if (res.ok) return await res.blob();
      } catch {}
      return null;
    };

    const getExt = (url: string, blob: Blob) => {
      const m = url.match(/\.(\w{2,5})(\?|$)/);
      if (m) return m[1].toLowerCase();
      if (blob.type.includes('jpeg') || blob.type.includes('jpg')) return 'jpg';
      if (blob.type.includes('png')) return 'png';
      if (blob.type.includes('webp')) return 'webp';
      return 'jpg';
    };

    const globalPhotos = (inspection.photos || []).filter(p => p.image_url);
    const defectImgs = (inspection.defects || []).flatMap(d => d.evidence_images || []).filter(Boolean);
    const checklistImgs = (inspection.checklists || []).flatMap(c => c.evidence_images || []).filter(Boolean);
    const skuEntries: { folderName: string; url: string; label: string }[] = [];
    for (const sku of inspection.skus || []) {
      const skuName = (sku.sku_code || sku.sku_name || sku.id.slice(0, 6)).replace(/[^a-zA-Z0-9_\-]/g, '_');
      for (const p of [...(sku.photoTiles || []), ...(sku.photo_tiles || []), ...(sku.photos || [])]) {
        if (p.image_url) skuEntries.push({ folderName: `SKU_Photos/${skuName}`, url: p.image_url, label: p.label || p.tile_label || p.tile_key || 'Photo' });
      }
      for (const d of sku.defects || []) {
        for (const img of d.evidence_images || []) {
          if (img) defectImgs.push(img);
        }
      }
    }

    const total = globalPhotos.length + skuEntries.length + defectImgs.length + checklistImgs.length;
    if (total === 0) { alert('No images found to download.'); return; }

    setDownloadingImages(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      if (globalPhotos.length > 0) {
        const folder = zip.folder('Inspection_Photos')!;
        for (let i = 0; i < globalPhotos.length; i++) {
          const blob = await fetchBlob(globalPhotos[i].image_url);
          if (blob) {
            const ext = getExt(globalPhotos[i].image_url, blob);
            const lbl = (globalPhotos[i].label || `Photo_${i + 1}`).replace(/[^a-zA-Z0-9_\-]/g, '_');
            folder.file(`${String(i + 1).padStart(2, '0')}_${lbl}.${ext}`, blob);
          }
        }
      }
      for (const { folderName, url, label } of skuEntries) {
        const blob = await fetchBlob(url);
        if (blob) {
          const ext = getExt(url, blob);
          zip.folder(folderName)!.file(`${label.replace(/[^a-zA-Z0-9_\-]/g, '_')}.${ext}`, blob);
        }
      }
      if (defectImgs.length > 0) {
        const folder = zip.folder('Defect_Evidence')!;
        for (let i = 0; i < defectImgs.length; i++) {
          const blob = await fetchBlob(defectImgs[i]);
          if (blob) folder.file(`${String(i + 1).padStart(2, '0')}_Defect.${getExt(defectImgs[i], blob)}`, blob);
        }
      }
      if (checklistImgs.length > 0) {
        const folder = zip.folder('Checklist_Evidence')!;
        for (let i = 0; i < checklistImgs.length; i++) {
          const blob = await fetchBlob(checklistImgs[i]);
          if (blob) folder.file(`${String(i + 1).padStart(2, '0')}_Checklist.${getExt(checklistImgs[i], blob)}`, blob);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `Final-Inspection-Images-${inspection.po_number || inspection.id.slice(0, 8)}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err: any) {
      console.error('Image download failed:', err);
      alert(`Image download failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setDownloadingImages(false);
    }
  };

  const checklistByCategory = useMemo(() => {
    const groups = new Map<string, ChecklistItem[]>();
    (inspection?.checklists || []).forEach(c => {
      const key = c.category || 'General';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    });
    return Array.from(groups.entries());
  }, [inspection]);

  const getSkuSampleSize = (qty: number) => {
    if (qty <= 8) return 2;
    if (qty <= 15) return 3;
    if (qty <= 25) return 5;
    if (qty <= 50) return 8;
    if (qty <= 90) return 13;
    if (qty <= 150) return 20;
    if (qty <= 280) return 32;
    if (qty <= 500) return 50;
    if (qty <= 1200) return 80;
    if (qty <= 3200) return 125;
    if (qty <= 10000) return 200;
    if (qty <= 35000) return 315;
    if (qty <= 150000) return 500;
    return 800;
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-7xl pb-12 flex items-center justify-center py-24">
        <div className="text-slate-500 text-sm font-medium">Loading inspection...</div>
      </div>
    );
  }

  if (error || !inspection) {
    return (
      <div className="mx-auto w-full max-w-7xl pb-12 flex flex-col items-center justify-center py-24 gap-3">
        <span className="material-symbols-outlined text-red-500 text-5xl">error</span>
        <div className="text-slate-700 text-sm font-semibold">{error || 'Inspection not found'}</div>
        <Link to="/final-inspection-reports" className="text-[#136bce] text-sm font-medium hover:underline">
          Back to Final Reports
        </Link>
      </div>
    );
  }

  const result = inspection.overall_result || 'PENDING';
  const isPass = result === 'PASS';
  const isFail = result === 'FAIL';
  const bannerBg = isPass ? 'bg-[#10b981]' : isFail ? 'bg-red-500' : 'bg-slate-500';
  const bannerGradient = isPass ? 'from-emerald-400 to-emerald-600' : isFail ? 'from-red-400 to-red-600' : 'from-slate-400 to-slate-600';
  const bannerIcon = isPass ? 'verified' : isFail ? 'error' : 'hourglass';
  const bannerSubtext = isPass ? 'Meets acceptable quality limits (AQL)' : isFail ? 'Does not meet AQL requirements' : 'Inspection pending completion';
  const statusLabel = inspection.status === 'COMPLETED' ? 'Inspection Complete' : inspection.status === 'IN_PROGRESS' ? 'In Progress' : 'Pending';

  const inspectorName = inspection.inspector?.name || inspection.inspector_name || 'Unknown';
  const inspectorInitials = getInitials(inspectorName);
  const reportIdShort = `QFI ${inspection.id.slice(0, 6).toUpperCase()}`;

  const skus = inspection.skus || [];
  const globalAudits = inspection.audits || [];
  const globalDefects = inspection.defects || [];
  const globalPhotos = inspection.photos || [];

  const majorDefectCount = globalDefects.filter(d => (d.level || d.severity || '').toLowerCase() === 'major').reduce((s, d) => s + (d.count || 1), 0);
  const minorDefectCount = globalDefects.filter(d => (d.level || d.severity || '').toLowerCase() === 'minor').reduce((s, d) => s + (d.count || 1), 0);

  return (
    <div className="mx-auto w-full max-w-7xl pb-12 animate-in fade-in duration-500 print:pb-0 print:max-w-none print:w-full print:animate-none print:p-0 space-y-8">
      {/* Header */}
      <div className="flex flex-col items-start sm:flex-row sm:items-center justify-between gap-4 print:mb-8">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1 font-medium print:hidden">
            <Link to="/final-inspection-reports" className="hover:text-[#136bce]">Final Reports</Link>
            <span className="material-symbols-outlined text-[14px]">chevron_right</span>
            <span className="text-[#136bce]">#{inspection.po_number || inspection.id.slice(0, 8)}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Final Inspection Report</h1>
            {inspection.is_recovered && (
              <span
                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ring-1 ring-inset ${inspection.recovery_incomplete ? 'bg-amber-50 text-amber-700 ring-amber-600/20' : 'bg-blue-50 text-[#136bce] ring-blue-600/20'}`}
              >
                <span className="material-symbols-outlined text-[14px]">restore_page</span>
                {inspection.recovery_incomplete ? 'Recovered · Incomplete' : 'Recovered'}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-3 w-full sm:w-auto print:hidden">
          <button
            onClick={() => setPdfModalOpen(true)}
            disabled={generatingPDF}
            className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-6 py-2.5 text-sm font-bold text-white shadow-md shadow-emerald-500/30 transition-all hover:bg-emerald-700 active:scale-95 flex-1 sm:flex-none disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">
              {generatingPDF ? 'hourglass_top' : 'picture_as_pdf'}
            </span>
            {generatingPDF ? 'Generating PDF…' : 'Generate PDF'}
          </button>
          <button
            onClick={handleDownloadImages}
            disabled={downloadingImages}
            className="flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-6 py-2.5 text-sm font-bold text-white shadow-md shadow-violet-500/30 transition-all hover:bg-violet-700 active:scale-95 flex-1 sm:flex-none disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">{downloadingImages ? 'hourglass_top' : 'photo_library'}</span>
            {downloadingImages ? 'Zipping…' : 'Download Images'}
          </button>
          <button onClick={handleDownload} className="flex items-center justify-center gap-2 rounded-lg bg-[#136bce] px-6 py-2.5 text-sm font-bold text-white shadow-md shadow-blue-500/30 transition-all hover:bg-blue-700 active:scale-95 flex-1 sm:flex-none">
            <span className="material-symbols-outlined text-[20px]">download</span>
            Download Report
          </button>
        </div>
      </div>

      {/* Result Status Banner */}
      <div className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden flex flex-col md:flex-row h-auto md:h-44 print:flex-row print:h-44 print:overflow-visible print:border-slate-300">
        <div className={`md:w-1/3 ${bannerBg} text-white p-6 flex flex-col items-center justify-center text-center relative overflow-hidden print:w-1/3`}>
          <div className={`absolute inset-0 bg-gradient-to-br ${bannerGradient} opacity-100 z-0 print:hidden`}></div>
          <span className="material-symbols-outlined absolute -right-6 -bottom-6 text-[140px] opacity-20 rotate-12 select-none z-0">{bannerIcon}</span>
          <div className="relative z-10 w-full flex flex-col items-center justify-center h-full">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-2 shadow-sm ring-1 ring-white/30">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              {statusLabel}
            </div>
            <h2 className="text-5xl font-black tracking-tighter drop-shadow-sm leading-none mb-1">{result}</h2>
            <p className="text-white/90 text-xs font-medium opacity-90">{bannerSubtext}</p>
          </div>
        </div>
        <div className="md:w-2/3 p-6 flex items-center justify-around bg-white relative print:w-2/3 print:justify-evenly print:p-2">
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Major Defects</p>
            <div className="flex items-baseline justify-center gap-2 print:gap-1">
              <p className="text-4xl font-black text-slate-800 group-hover:text-red-500 transition-colors">{String(majorDefectCount).padStart(2, '0')}</p>
              <span className="text-sm font-bold text-slate-400">units</span>
            </div>
          </div>
          <div className="w-px h-20 bg-slate-100 mx-4 print:mx-1"></div>
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Minor Defects</p>
            <div className="flex items-baseline justify-center gap-2 print:gap-1">
              <p className="text-4xl font-black text-slate-800 group-hover:text-amber-500 transition-colors">{String(minorDefectCount).padStart(2, '0')}</p>
              <span className="text-sm font-bold text-slate-400">units</span>
            </div>
          </div>
          <div className="w-px h-20 bg-slate-100 mx-4 print:mx-1"></div>
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Lot Size</p>
            <div className="flex items-baseline justify-center gap-2 print:gap-1">
              <p className="text-4xl font-black text-slate-800 group-hover:text-emerald-500 transition-colors">{inspection.lot_size || 0}</p>
              <span className="text-sm font-bold text-slate-400">units</span>
            </div>
          </div>
          <div className="w-px h-20 bg-slate-100 mx-4 print:mx-1"></div>
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Report ID</p>
            <div className="flex items-baseline justify-center gap-2 print:gap-1">
              <p className="text-2xl font-black text-slate-800 group-hover:text-[#136bce] transition-colors uppercase tracking-tight">{reportIdShort}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Order & Inspector Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm print:shadow-none break-inside-avoid">
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-widest mb-4 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#136bce] text-[18px]">info</span>Order Information
          </h2>
          <div className="grid grid-cols-2 gap-y-6 gap-x-4">
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Customer</p>
              <p className="text-sm font-semibold text-slate-800">{inspection.customer_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">PO Number</p>
              <p className="text-sm font-semibold text-[#136bce]">{inspection.po_number || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Assigned To</p>
              <div className="flex items-center gap-2">
                {inspection.inspector?.profileImage ? (
                  <img src={inspection.inspector.profileImage} alt={inspectorName} className="h-6 w-6 rounded-full object-cover" />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">{inspectorInitials}</div>
                )}
                <p className="text-sm font-semibold text-slate-800">{inspectorName}</p>
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Ship Date</p>
              <p className="text-sm font-semibold text-slate-800">{formatDate(inspection.po_ship_date)}</p>
            </div>
            <div className="col-span-2 pt-2 border-t border-slate-100 mt-2">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Item Description</p>
              <p className="text-sm text-slate-600 font-medium leading-relaxed">
                {inspection.product_category || inspection.po_notes || 'No description available'}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col justify-between print:shadow-none break-inside-avoid">
          <div>
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-widest mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce] text-[18px]">person</span>Inspector Information
            </h2>
            <div className="mb-6">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Inspector Name</p>
              <p className="text-lg font-bold text-slate-800">{inspectorName}</p>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wide">QA Inspector</p>
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 print:bg-slate-50">
            <h3 className="text-[10px] font-bold text-slate-700 mb-3 uppercase tracking-widest">AQL Sampling Summary</h3>
            <div className="flex justify-between gap-2">
              <div className="text-center p-2 bg-white rounded shadow-sm flex-1 print:border">
                <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-tighter">Level</span>
                <span className="block text-lg font-bold text-slate-800">{inspection.inspection_level || 'N/A'}</span>
              </div>
              <div className="text-center p-2 bg-white rounded shadow-sm flex-1 print:border">
                <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-tighter">Major</span>
                <span className="block text-lg font-bold text-slate-800">{inspection.aql_major || 'N/A'}</span>
              </div>
              <div className="text-center p-2 bg-white rounded shadow-sm flex-1 print:border">
                <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-tighter">Minor</span>
                <span className="block text-lg font-bold text-slate-800">{inspection.aql_minor || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SKU Summary Table */}
      {skus.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm print:shadow-none break-inside-avoid">
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-widest mb-4 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#136bce] text-[18px]">inventory_2</span>SKU Summary
          </h2>
          <div className="overflow-x-auto print:overflow-visible">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 font-bold">
                <tr>
                  <th className="px-4 py-3 rounded-l-lg">SKU / Style</th>
                  <th className="px-4 py-3">Vendor Code</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3 text-center">Order Qty</th>
                  <th className="px-4 py-3 text-center">No of Bundles</th>
                  <th className="px-4 py-3 text-center">Inspected Qty</th>
                  <th className="px-4 py-3 text-center">Sample Size</th>
                  <th className="px-4 py-3 text-right rounded-r-lg">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-900 font-medium">
                {skus.map((sku) => {
                  const qty = sku.quantity ?? sku.ordered_qty ?? 0;
                  const status = (sku.result || 'pending').toUpperCase();
                  const statusClass = status === 'PASS' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : status === 'FAIL' ? 'bg-red-50 text-red-700 border-red-100' : 'bg-slate-50 text-slate-600 border-slate-200';
                  const colour = sku.colour || sku.color || '';
                  const size = sku.size || '';
                  const variant = [colour, size ? `Size: ${size}` : ''].filter(Boolean).join(' | ');
                  return (
                    <tr key={sku.id} className="print:break-inside-avoid">
                      <td className="px-4 py-4 font-mono font-bold text-slate-800 align-middle text-xs">{sku.sku_code || sku.sku_name || 'N/A'}</td>
                      <td className="px-4 py-4 align-middle text-sm text-slate-600">{sku.vendorStyle || sku.vendor_style || '—'}</td>
                      <td className="px-4 py-4 align-middle">
                        <div className="font-semibold text-sm">{sku.description || sku.sku_description || '—'}</div>
                        {variant && <div className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">{variant}</div>}
                      </td>
                      <td className="px-4 py-4 text-center align-middle text-sm">{qty}</td>
                      <td className="px-4 py-4 text-center align-middle text-sm">{Math.ceil(qty / 5)}</td>
                      <td className="px-4 py-4 text-center align-middle text-sm">{sku.inspected_qty || qty}</td>
                      <td className="px-4 py-4 text-center align-middle text-sm">{getSkuSampleSize(qty)}</td>
                      <td className="px-4 py-4 text-right align-middle">
                        <span className={`inline-flex items-center justify-center min-w-[80px] px-2 py-1 rounded-md text-[10px] font-bold border ${statusClass}`}>{status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Checklist & Verifications */}
      {checklistByCategory.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm print:shadow-none break-inside-avoid">
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-widest mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#136bce] text-[18px]">checklist</span>Checklist &amp; Verifications
          </h2>
          <div className="space-y-8">
            {checklistByCategory.map(([category, items]) => (
              <div key={category}>
                <h3 className="flex items-center text-[#136bce] font-bold text-[10px] mb-4 uppercase tracking-widest">
                  <span className="material-symbols-outlined mr-2 text-lg">science</span>{category}
                </h3>
                <div className="bg-slate-50 rounded-lg p-6 space-y-6 print:bg-slate-50 print:border">
                  {items.map((item, idx) => {
                    const status = (item.status || 'na').toLowerCase();
                    const badgeClass = status === 'pass' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : status === 'fail' ? 'bg-red-50 text-red-700 border-red-100' : 'bg-slate-200 text-slate-600 border-slate-300';
                    const badgeIcon = status === 'pass' ? 'check' : status === 'fail' ? 'close' : 'remove';
                    const badgeLabel = status === 'pass' ? 'YES' : status === 'fail' ? 'NO' : 'N/A';
                    const hasExtras = (item.remarks && item.remarks.trim().length > 0) || (item.evidence_images && item.evidence_images.length > 0);
                    return (
                      <div key={item.id} className={`flex flex-col md:flex-row ${hasExtras ? 'justify-between' : 'md:items-center justify-between'} gap-6 ${idx < items.length - 1 ? 'border-b border-slate-200 pb-6' : ''} print:break-inside-avoid`}>
                        <div className="flex-1">
                          <p className="text-lg font-bold text-slate-800 mb-3">{item.title}</p>
                          {hasExtras && (
                            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm print:border">
                              {item.remarks && (
                                <p className="text-xs text-slate-600 mb-3 font-medium">
                                  <span className="font-bold text-slate-800">Remarks:</span> {item.remarks}
                                </p>
                              )}
                              {item.evidence_images && item.evidence_images.length > 0 && (
                                <div className="flex gap-3 mt-3 overflow-x-auto pb-1 print:overflow-visible">
                                  {item.evidence_images.map((src, i) => (
                                    <div key={i} className="h-24 w-24 bg-slate-200 rounded-lg overflow-hidden ring-1 ring-slate-200 flex-shrink-0 print:border cursor-zoom-in" onClick={() => openPreview(src)}>
                                      <img alt="Verification Thumb" className="w-full h-full object-cover" src={src} />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className={`flex items-center gap-4 ${hasExtras ? 'self-start md:self-center' : ''}`}>
                          <span className={`inline-flex items-center justify-center min-w-[140px] px-6 py-2.5 rounded-lg text-sm font-bold border uppercase tracking-wider ${badgeClass}`}>
                            <span className="material-symbols-outlined mr-2 text-[20px]">{badgeIcon}</span>{badgeLabel}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detailed SKU Report */}
      {skus.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm print:shadow-none break-inside-avoid">
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-widest mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#136bce] text-[18px]">manage_search</span>Detailed SKU Report
          </h2>
          <div className="space-y-8">
            {skus.map((sku) => {
              const skuId = sku.po_sku_id || sku.id;
              const skuName = sku.sku_name || sku.sku_code || '—';
              const qty = sku.quantity ?? sku.ordered_qty ?? 0;
              const colour = sku.colour || sku.color || '';
              const size = sku.size || '';
              const vendorStyle = sku.vendorStyle || sku.vendor_style || '';
              const completion = (sku.productionCompletion ?? sku.production_completion ?? 0) as number;
              const completionPct = Math.min(Math.max(completion, 0), 100);
              const skuResult = (sku.result || '').toLowerCase();

              const skuAudits = skuId ? globalAudits.filter(a => a.sku_id === skuId) : [];
              const metalAudit = skuAudits.find(a => a.type === 'METAL_DETECTION');
              let metalDetection = sku.metal_detection || sku.metalDetection || '';
              if (!metalDetection && metalAudit) metalDetection = (metalAudit.data?.result || metalAudit.result || '') as string;

              const skuPhotos = skuId
                ? globalPhotos.filter(p => p.sku_id === skuId)
                : globalPhotos.length === 0 ? [...(sku.photoTiles || sku.photo_tiles || sku.photos || [])] : [];

              const skuDefects = skuId
                ? globalDefects.filter(d => d.sku_id === skuId)
                : globalDefects.length === 0 ? [...(sku.defects || [])] : [];

              const allPhotosWithUrl = skuPhotos.filter(p => p.image_url);
              const mandatoryPhotos = allPhotosWithUrl.filter(p => p.is_mandatory || p.required);
              const optionalPhotos = allPhotosWithUrl.filter(p => !p.is_mandatory && !p.required);

              return (
                <div key={sku.id} id={`sku-${skuId}`} className={`bg-slate-50 rounded-xl p-6 border print:bg-slate-50 print:border break-inside-avoid ${skuId === focusSku ? 'border-[#136bce] ring-2 ring-[#136bce] ring-offset-2' : 'border-slate-100'}`}>
                  {/* SKU Header */}
                  <div className="flex items-center gap-3 mb-6">
                    <span className="material-symbols-outlined text-[#136bce] text-3xl">inventory_2</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-bold text-slate-800 text-lg tracking-tight">SKU: {skuName}</h3>
                        {skuResult && (
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${skuResult === 'pass' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-700 border-red-100'}`}>
                            {skuResult.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        {vendorStyle && <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Vendor: <span className="text-slate-700">{vendorStyle}</span></div>}
                        {size && <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Size: <span className="text-slate-700">{size}</span></div>}
                        {colour && <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Colour: <span className="text-slate-700">{colour}</span></div>}
                      </div>
                    </div>
                  </div>

                  {/* Production Completion */}
                  {completion > 0 && (
                    <div className="mb-6 bg-white rounded-xl p-4 border border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="material-symbols-outlined text-[14px]">trending_up</span>Production Completion
                        </p>
                        <p className={`text-sm font-bold ${completionPct >= 90 ? 'text-emerald-600' : completionPct >= 60 ? 'text-amber-500' : 'text-red-500'}`}>{completionPct.toFixed(0)}%</p>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${completionPct >= 90 ? 'bg-emerald-500' : completionPct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${completionPct}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Mandatory Photos */}
                  {mandatoryPhotos.length > 0 && (
                    <div className="mb-8 print:break-inside-avoid">
                      <h4 className="text-[10px] font-bold text-slate-500 uppercase mb-4 flex items-center gap-2 tracking-widest">
                        <span className="material-symbols-outlined text-sm">photo_camera</span> Mandatory Images
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 print:grid-cols-4">
                        {mandatoryPhotos.map((photo, i) => (
                          <div key={photo.id || i} className="space-y-3 print:break-inside-avoid">
                            <div className="aspect-square rounded-xl bg-white border border-slate-200 overflow-hidden relative group shadow-sm print:shadow-none cursor-zoom-in" onClick={() => openPreview(photo.image_url)}>
                              <img alt={photo.label || 'Photo'} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" src={photo.image_url} />
                            </div>
                            <p className="text-[10px] text-center text-slate-500 font-bold uppercase tracking-tight leading-tight">{photo.label || photo.tile_label || photo.tile_key || 'Photo'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Optional Photos */}
                  {optionalPhotos.length > 0 && (
                    <div className="mb-8 print:break-inside-avoid">
                      <h4 className="text-[10px] font-bold text-slate-500 uppercase mb-4 flex items-center gap-2 tracking-widest">
                        <span className="material-symbols-outlined text-sm">photo_library</span> Optional Images
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 print:grid-cols-4">
                        {optionalPhotos.map((photo, i) => (
                          <div key={photo.id || i} className="space-y-3 print:break-inside-avoid">
                            <div className="aspect-square rounded-xl bg-white border border-slate-200 overflow-hidden relative group shadow-sm print:shadow-none cursor-zoom-in" onClick={() => openPreview(photo.image_url)}>
                              <img alt={photo.label || 'Photo'} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" src={photo.image_url} />
                            </div>
                            <p className="text-[10px] text-center text-slate-500 font-bold uppercase tracking-tight leading-tight">{photo.label || photo.tile_label || photo.tile_key || 'Photo'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Audit Sections (Measurement, Weight, Metal Detection) */}
                  {(skuAudits.length > 0 || metalDetection) && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                      {/* Measurement Audit */}
                      {(() => {
                        const audit = skuAudits.find(a => a.type === 'MEASUREMENT');
                        const auditData = audit?.data || {};
                        const sampleSize = auditData.sampleSize ?? auditData.sample_size;
                        const passedQty = auditData.passedQty ?? auditData.passed_qty;
                        const auditResult = (audit?.result || '').toUpperCase();
                        const notes = audit?.notes || audit?.remarks || auditData.notes || auditData.remarks || '';
                        const images: string[] = Array.isArray(auditData.imageUrls) ? auditData.imageUrls : Array.isArray(auditData.image_urls) ? auditData.image_urls : Array.isArray(auditData.evidence_images) ? auditData.evidence_images : [];
                        const descs: string[] = Array.isArray(auditData.imageDescriptions) ? auditData.imageDescriptions : Array.isArray(auditData.image_descriptions) ? auditData.image_descriptions : [];
                        if (!audit) return null;
                        return (
                          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm print:break-inside-avoid flex flex-col">
                            <h4 className="text-[10px] font-bold text-slate-500 uppercase mb-4 flex items-center gap-2 tracking-widest">
                              <span className="material-symbols-outlined text-sm">straighten</span> Measurement Audit
                            </h4>
                            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4">
                              {sampleSize !== undefined && <div className="flex flex-col"><span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Sample Size</span><span className="text-sm font-black text-slate-800">{sampleSize}</span></div>}
                              {passedQty !== undefined && passedQty !== null && <div className="flex flex-col"><span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Passed Qty</span><span className="text-sm font-black text-slate-800">{sampleSize !== undefined ? `${passedQty} / ${sampleSize}` : `${passedQty}`}</span></div>}
                              <div className="flex flex-col">
                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Status</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider w-fit ${auditResult === 'PASS' ? 'bg-emerald-100 text-emerald-700' : auditResult === 'FAIL' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{auditResult || 'N/A'}</span>
                              </div>
                            </div>
                            {images.length > 0 && (
                              <div className="grid grid-cols-2 gap-2 mb-3">
                                {images.map((src, idx) => (
                                  <div key={idx} className="flex flex-col gap-1">
                                    <div className="aspect-square rounded-lg bg-slate-100 border border-slate-200 overflow-hidden cursor-zoom-in group relative" onClick={() => openPreview(src)}>
                                      <img alt={`Measurement ${idx + 1}`} className="w-full h-full object-cover transition-transform group-hover:scale-110" src={src} />
                                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                        <span className="material-symbols-outlined text-white opacity-0 group-hover:opacity-100 transition-opacity">zoom_in</span>
                                      </div>
                                    </div>
                                    {descs[idx] && <p className="text-[9px] text-slate-500 font-medium leading-tight px-1 italic">{descs[idx]}</p>}
                                  </div>
                                ))}
                              </div>
                            )}
                            {notes && <p className="text-[11px] text-slate-600 font-medium leading-relaxed italic mt-auto">{notes}</p>}
                          </div>
                        );
                      })()}

                      {/* Weight Audit */}
                      {(() => {
                        const audit = skuAudits.find(a => a.type === 'WEIGHT');
                        const auditData = audit?.data || {};
                        const sampleSize = auditData.sampleSize ?? auditData.sample_size;
                        const passedQty = auditData.passedQty ?? auditData.passed_qty;
                        const auditResult = (audit?.result || '').toUpperCase();
                        const notes = audit?.notes || audit?.remarks || auditData.notes || auditData.remarks || '';
                        const images: string[] = Array.isArray(auditData.imageUrls) ? auditData.imageUrls : Array.isArray(auditData.image_urls) ? auditData.image_urls : Array.isArray(auditData.evidence_images) ? auditData.evidence_images : [];
                        const descs: string[] = Array.isArray(auditData.imageDescriptions) ? auditData.imageDescriptions : Array.isArray(auditData.image_descriptions) ? auditData.image_descriptions : [];
                        if (!audit) return null;
                        return (
                          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm print:break-inside-avoid flex flex-col">
                            <h4 className="text-[10px] font-bold text-slate-500 uppercase mb-4 flex items-center gap-2 tracking-widest">
                              <span className="material-symbols-outlined text-sm">scale</span> Weight Audit
                            </h4>
                            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4">
                              {sampleSize !== undefined && <div className="flex flex-col"><span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Sample Size</span><span className="text-sm font-black text-slate-800">{sampleSize}</span></div>}
                              {passedQty !== undefined && passedQty !== null && <div className="flex flex-col"><span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Passed Qty</span><span className="text-sm font-black text-slate-800">{sampleSize !== undefined ? `${passedQty} / ${sampleSize}` : `${passedQty}`}</span></div>}
                              <div className="flex flex-col">
                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Status</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider w-fit ${auditResult === 'PASS' ? 'bg-emerald-100 text-emerald-700' : auditResult === 'FAIL' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{auditResult || 'N/A'}</span>
                              </div>
                            </div>
                            {images.length > 0 && (
                              <div className="grid grid-cols-2 gap-2 mb-3">
                                {images.map((src, idx) => (
                                  <div key={idx} className="flex flex-col gap-1">
                                    <div className="aspect-square rounded-lg bg-slate-100 border border-slate-200 overflow-hidden cursor-zoom-in group relative" onClick={() => openPreview(src)}>
                                      <img alt={`Weight ${idx + 1}`} className="w-full h-full object-cover transition-transform group-hover:scale-110" src={src} />
                                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                        <span className="material-symbols-outlined text-white opacity-0 group-hover:opacity-100 transition-opacity">zoom_in</span>
                                      </div>
                                    </div>
                                    {descs[idx] && <p className="text-[9px] text-slate-500 font-medium leading-tight px-1 italic">{descs[idx]}</p>}
                                  </div>
                                ))}
                              </div>
                            )}
                            {notes && <p className="text-[11px] text-slate-600 font-medium leading-relaxed italic mt-auto">{notes}</p>}
                          </div>
                        );
                      })()}

                      {/* Metal Detection */}
                      <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm print:break-inside-avoid flex flex-col">
                        <h4 className="text-[10px] font-bold text-slate-500 uppercase mb-4 flex items-center gap-2 tracking-widest">
                          <span className="material-symbols-outlined text-sm">precision_manufacturing</span> Metal Detection
                        </h4>
                        <div className="flex flex-col items-center justify-center flex-1 py-4">
                          {metalDetection ? (
                            <>
                              <div className={`flex items-center gap-2 px-6 py-3 rounded-xl font-black text-base tracking-widest shadow-sm mb-4 border ${metalDetection.toLowerCase() === 'pass' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-700 border-red-100'}`}>
                                <span className="material-symbols-outlined text-2xl">{metalDetection.toLowerCase() === 'pass' ? 'verified' : 'error'}</span>
                                {metalDetection.toUpperCase()}
                              </div>
                              {metalAudit?.notes && <p className="text-[11px] text-slate-600 font-bold text-center px-4 leading-relaxed">{metalAudit.notes}</p>}
                            </>
                          ) : (
                            <span className="text-[11px] text-slate-400 font-medium italic">No data recorded</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Defect Findings */}
                  {skuDefects.length > 0 && (
                    <div className="print:break-inside-avoid">
                      <h4 className="text-[10px] font-bold text-red-500 uppercase mb-4 flex items-center gap-2 tracking-widest">
                        <span className="material-symbols-outlined text-sm">warning</span> Defect Findings
                      </h4>
                      <div className="space-y-4">
                        {skuDefects.map((defect) => {
                          const level = (defect.level || defect.severity || 'major').toUpperCase();
                          const levelClass = level === 'MAJOR' ? 'bg-red-100 text-red-700' : level === 'MINOR' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
                          const borderClass = level === 'MAJOR' ? 'border-red-200' : level === 'MINOR' ? 'border-amber-200' : 'border-slate-200';
                          const badgeBg = level === 'MAJOR' ? 'bg-red-600' : level === 'MINOR' ? 'bg-amber-600' : 'bg-slate-600';
                          return (
                            <div key={defect.id} className="bg-red-50/50 rounded-xl p-5 border border-red-100 shadow-sm print:bg-red-50/50 print:border">
                              <div className="flex flex-col md:flex-row gap-8">
                                <div className="flex-1">
                                  <h5 className="text-lg font-bold text-slate-800 mb-3 tracking-tight">{defect.title || defect.type || 'Defect'}</h5>
                                  <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm mb-5">
                                    {defect.category && (<><span className="font-bold text-slate-500 uppercase text-[10px] pt-0.5">Category:</span><span className="text-slate-800 font-bold">{defect.category}</span></>)}
                                    {defect.type && (<><span className="font-bold text-slate-500 uppercase text-[10px] pt-0.5">Type:</span><span className="text-slate-800 font-bold">{defect.type}</span></>)}
                                    <span className="font-bold text-slate-500 uppercase text-[10px] pt-0.5">Severity:</span>
                                    <span className={`inline-flex items-center px-3 py-1 rounded text-[10px] font-bold w-fit uppercase tracking-wider ${levelClass}`}>{level}</span>
                                    <span className="font-bold text-slate-500 uppercase text-[10px] pt-0.5">Count:</span>
                                    <span className="text-slate-800 font-bold">{defect.count || 1}</span>
                                    {defect.location && (<><span className="font-bold text-slate-500 uppercase text-[10px] pt-0.5">Location:</span><span className="text-slate-800 font-bold">{defect.location}</span></>)}
                                  </div>
                                  {defect.description && (
                                    <div className="bg-white p-4 rounded-lg border border-red-100 shadow-sm print:border">
                                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-2 tracking-widest">Remarks</p>
                                      <p className="text-sm text-slate-600 italic font-medium leading-relaxed">"{defect.description}"</p>
                                    </div>
                                  )}
                                </div>
                                {defect.evidence_images && defect.evidence_images.length > 0 && (
                                  <div className="flex gap-4 overflow-x-auto pb-2 md:pb-0 print:overflow-visible">
                                    {defect.evidence_images.map((src, i) => (
                                      <div key={i} className={`h-40 w-40 shrink-0 rounded-xl overflow-hidden border-2 ${borderClass} relative group cursor-zoom-in shadow-md print:shadow-none print:break-inside-avoid`} onClick={() => openPreview(src)}>
                                        <img alt="Defect" className="w-full h-full object-cover transition-transform group-hover:scale-110" src={src} />
                                        <div className={`absolute top-2 right-2 text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase shadow-sm ${badgeBg}`}>{level}</div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {skuDefects.length === 0 && (
                    <div className="text-sm text-slate-500 italic font-bold flex items-center gap-2 bg-emerald-50 w-fit px-4 py-2 rounded-lg border border-emerald-100 print:bg-emerald-50">
                      <span className="material-symbols-outlined text-emerald-600 text-[20px]">check_circle</span>
                      <span className="text-emerald-800">No defects found for this SKU.</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Final Remarks & Signatures */}
      <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm print:shadow-none break-inside-avoid">
        <h2 className="text-sm font-bold text-slate-800 uppercase tracking-widest mb-8 flex items-center gap-2">
          <span className="material-symbols-outlined text-[#136bce] text-[18px]">comment</span>Final Remarks
        </h2>
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 mb-8 shadow-inner print:bg-slate-50">
          <p className="w-full text-base font-medium text-slate-700 min-h-[60px] whitespace-pre-wrap">
            {inspection.remarks || <span className="italic text-slate-400">No final remarks provided.</span>}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 border-t border-slate-100 pt-8">
          <div className="space-y-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">QA Rep Signature</p>
            <div className="h-16 border-b border-slate-200 flex items-end justify-center pb-2">
              {inspection.inspector_signature_url ? (
                <img src={inspection.inspector_signature_url} alt="QA Signature" className="max-h-14 object-contain" />
              ) : (
                <span className="font-serif italic text-xl text-slate-300 select-none opacity-50">Pending</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-[#136bce] text-white flex items-center justify-center font-bold text-[10px]">{inspectorInitials}</div>
              <p className="text-sm font-bold text-slate-900">{inspectorName}</p>
            </div>
          </div>
          <div className="space-y-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Factory Rep Signature</p>
            <div className="h-16 border-b border-slate-200 flex items-end justify-center pb-2">
              {inspection.factory_rep_signature_url ? (
                <img src={inspection.factory_rep_signature_url} alt="Factory Signature" className="max-h-14 object-contain" />
              ) : (
                <span className="font-serif italic text-xl text-slate-300 select-none opacity-50">Pending</span>
              )}
            </div>
            <p className="text-sm font-bold text-slate-900">Factory Quality Manager</p>
          </div>
          <div className="space-y-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Inspection Date</p>
            <div className="h-16 flex items-end pb-2">
              <p className="text-xl font-bold text-slate-900">{formatDate(inspection.created_at)}</p>
            </div>
            <p className="text-sm font-medium text-slate-500 italic">Date of Final Audit</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-[11px] text-slate-400 font-bold px-2 tracking-wide uppercase print:mt-20 print:border-t print:pt-6 print:border-slate-200">
        <span className="hidden print:block text-slate-300">System-Generated Document</span>
      </div>

      {/* PDF Generation Modal — premium animated */}
      {generatingPDF && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300 print:hidden">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            {/* Animated gradient top border */}
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-blue-500 to-emerald-400 bg-[length:200%_100%] animate-gradient-x" />

            {/* Decorative blurred orbs */}
            <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-emerald-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-blue-300/30 blur-3xl" />

            <div className="relative p-8 flex flex-col items-center text-center">
              {/* Animated icon */}
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center shadow-lg shadow-emerald-500/40">
                  <span className="material-symbols-outlined text-white text-[40px] animate-pulse">picture_as_pdf</span>
                </div>
                {/* Orbiting dot */}
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-emerald-500 shadow-md shadow-emerald-500/50" />
                </div>
              </div>

              {/* Heading */}
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">
                Generating your PDF
              </h2>

              {/* Description */}
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 max-w-xs">
                We're crafting a beautifully formatted inspection report. This usually takes just a few seconds.
              </p>

              {/* Rotating status message */}
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 mb-6 overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </div>
                  <p
                    key={pdfStatusIndex}
                    className="text-sm font-bold text-slate-700 text-left flex-1 animate-in fade-in slide-in-from-bottom-1 duration-500"
                  >
                    {pdfStatusMessages[pdfStatusIndex]}
                  </p>
                </div>
              </div>

              {/* Progress bar — real percentage while images are being
                  fetched/compressed; falls back to an indeterminate shimmer
                  once that's done and the PDF is being laid out (react-pdf
                  gives no progress signal for that final step). */}
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                {pdfProgress && pdfProgress.total > 0 ? (
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${Math.min(100, Math.round((pdfProgress.done / pdfProgress.total) * 100))}%` }}
                  />
                ) : (
                  <div className="h-full w-1/3 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full animate-pdf-progress" />
                )}
              </div>
              {pdfProgress && pdfProgress.total > 0 && (
                <p className="mt-2 text-[11px] font-bold text-slate-400">
                  {Math.min(pdfProgress.done, pdfProgress.total)} / {pdfProgress.total} images · {Math.min(100, Math.round((pdfProgress.done / pdfProgress.total) * 100))}%
                </p>
              )}

              {/* Footer tagline */}
              <p className="mt-6 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                Powered by <span className="text-emerald-600">QA Mate</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/90 backdrop-blur-sm p-4 animate-in fade-in duration-200 print:hidden"
          onClick={() => { setPreviewImage(null); setZoom(1); }}
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-slate-300 transition-colors p-2 rounded-full bg-slate-800/50 z-10"
            onClick={(e) => { e.stopPropagation(); setPreviewImage(null); setZoom(1); }}
          >
            <span className="material-symbols-outlined text-[32px]">close</span>
          </button>
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="text-white/70 hover:text-white transition-colors disabled:opacity-30" onClick={() => setZoom((prev) => Math.max(0.25, parseFloat((prev - 0.25).toFixed(2))))} disabled={zoom <= 0.25}>
              <span className="material-symbols-outlined text-[22px]">remove</span>
            </button>
            <span className="text-white text-xs font-bold tracking-widest w-12 text-center select-none">{Math.round(zoom * 100)}%</span>
            <button className="text-white/70 hover:text-white transition-colors disabled:opacity-30" onClick={() => setZoom((prev) => Math.min(5, parseFloat((prev + 0.25).toFixed(2))))} disabled={zoom >= 5}>
              <span className="material-symbols-outlined text-[22px]">add</span>
            </button>
            <div className="w-px h-4 bg-white/20 mx-1" />
            <button className="text-white/70 hover:text-white transition-colors" onClick={() => setZoom(1)}>
              <span className="material-symbols-outlined text-[20px]">fit_screen</span>
            </button>
          </div>
          <div ref={zoomContainerRef} className="flex items-center justify-center overflow-hidden max-h-[90vh] max-w-[90vw]" style={{ cursor: zoom > 1 ? 'grab' : 'zoom-in' }} onClick={(e) => e.stopPropagation()}>
            <img src={previewImage} alt="Preview" className="rounded-lg shadow-2xl animate-in zoom-in-95 duration-300 max-h-[82vh] max-w-[88vw] object-contain" style={{ transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform 0.1s ease' }} draggable={false} />
          </div>
        </div>
      )}

      {/* ───── Download Images Modal — premium animated ───── */}
      {downloadingImages && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300 print:hidden">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-400 via-fuchsia-500 to-violet-400 bg-[length:200%_100%] animate-gradient-x" />
            <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-violet-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-fuchsia-300/30 blur-3xl" />
            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-500/40">
                  <span className="material-symbols-outlined text-white text-[40px] animate-pulse">photo_library</span>
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-violet-500 shadow-md shadow-violet-500/50" />
                </div>
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Downloading Images</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 max-w-xs">
                Fetching and packaging all report images into a neatly organised ZIP folder.
              </p>
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 mb-6 overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-500" />
                  </div>
                  <p key={imageStatusIndex} className="text-sm font-bold text-slate-700 text-left flex-1 animate-in fade-in slide-in-from-bottom-1 duration-500">
                    {imageStatusMessages[imageStatusIndex]}
                  </p>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full animate-pdf-progress" />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                Powered by <span className="text-violet-600">QA Mate</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ───── PDF Template Selector Modal ───── */}
      {pdfModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 print:hidden"
          onClick={() => !generatingPDF && setPdfModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md animate-in zoom-in-95 fade-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-emerald-600 text-[22px]">picture_as_pdf</span>
                <h3 className="text-base font-bold text-slate-900">Generate PDF Report</h3>
              </div>
              <button
                onClick={() => setPdfModalOpen(false)}
                disabled={generatingPDF}
                className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
              >
                <span className="material-symbols-outlined text-[22px]">close</span>
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Template
                </label>
                <div className="relative" ref={templateDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsTemplateDropdownOpen(prev => !prev)}
                    disabled={generatingPDF}
                    className="w-full flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className={`h-6 w-6 rounded-full ${selectedTemplateOption.iconBg} flex items-center justify-center shrink-0`}>
                      <span className={`material-symbols-outlined text-[14px] ${selectedTemplateOption.iconColor}`}>
                        {selectedTemplateOption.icon}
                      </span>
                    </span>
                    <span className="truncate">{selectedTemplateOption.name}</span>
                    <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
                  </button>
                  {isTemplateDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-xl border border-slate-200 bg-white shadow-lg py-1 overflow-hidden">
                      {visibleTemplateOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { setSelectedTemplate(opt.value); setIsTemplateDropdownOpen(false); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${selectedTemplate === opt.value ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                        >
                          <span className={`h-6 w-6 rounded-full ${opt.iconBg} flex items-center justify-center shrink-0`}>
                            <span className={`material-symbols-outlined text-[14px] ${opt.iconColor}`}>
                              {opt.icon}
                            </span>
                          </span>
                          {opt.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-2 leading-snug">
                  {selectedTemplateOption.description}
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Compression Level
                </label>
                <div className="relative" ref={compressionDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsCompressionDropdownOpen(prev => !prev)}
                    disabled={generatingPDF}
                    className="w-full flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className={`h-6 w-6 rounded-full ${selectedCompressionOption.iconBg} flex items-center justify-center shrink-0`}>
                      <span className={`material-symbols-outlined text-[14px] ${selectedCompressionOption.iconColor}`}>
                        {selectedCompressionOption.icon}
                      </span>
                    </span>
                    <span className="truncate">{selectedCompressionOption.value}. {selectedCompressionOption.name}</span>
                    <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
                  </button>
                  {isCompressionDropdownOpen && (
                    <div className="absolute left-0 right-0 bottom-full mb-1 z-20 rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-64 overflow-y-auto">
                      {compressionOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { setSelectedCompression(opt.value); setIsCompressionDropdownOpen(false); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${selectedCompression === opt.value ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                        >
                          <span className={`h-6 w-6 rounded-full ${opt.iconBg} flex items-center justify-center shrink-0`}>
                            <span className={`material-symbols-outlined text-[14px] ${opt.iconColor}`}>
                              {opt.icon}
                            </span>
                          </span>
                          {opt.value}. {opt.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-2 leading-snug">
                  {selectedCompressionOption.description}
                </p>
              </div>

              {generatingPDF && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-600 text-[18px] animate-spin">progress_activity</span>
                  <span className="text-xs font-medium text-emerald-800">
                    {pdfStatusMessages[pdfStatusIndex]}
                  </span>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2 rounded-b-2xl">
              <button
                onClick={() => setPdfModalOpen(false)}
                disabled={generatingPDF}
                className="rounded-lg px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleGeneratePDF(selectedTemplate, selectedCompression);
                  setPdfModalOpen(false);
                }}
                disabled={generatingPDF}
                className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-bold text-white shadow-md shadow-emerald-500/30 hover:bg-emerald-700 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[18px]">
                  {generatingPDF ? 'hourglass_top' : 'picture_as_pdf'}
                </span>
                {generatingPDF ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinalInspectionReportDetail;
